import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import WgptBotToolsAPI from '../../api/wgpt/botTools';
import types from '../mutation-types';
import { throwErrorMessage } from '../utils/api';

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    isTesting: false,
    isCloning: false,
    isExporting: false,
    isImporting: false,
  },
  executions: {
    records: [],
    meta: {},
    uiFlags: { isFetching: false },
  },
};

export const getters = {
  getToolSets($state) {
    return $state.records;
  },
  getToolSet: $state => toolSetId => {
    const toolSet = $state.records.find(({ id }) => id === Number(toolSetId));
    return toolSet ?? {};
  },
  getTools: $state => toolSetId => {
    const toolSet = $state.records.find(({ id }) => id === Number(toolSetId));
    if (!toolSet) return [];

    const legacyTools = toolSet.config?.tools ?? [];
    const automationTools = toolSet.tools.map(tool => ({
      ...tool,
      type: 'function_automation',
    }));
    const tools = [...legacyTools, ...automationTools];
    return tools;
  },
  getTool: $state => (toolSetId, toolId) => {
    const toolSet = $state.records.find(({ id }) => id === Number(toolSetId));
    if (!toolSet) return {};

    const legacyTools = toolSet.config?.tools ?? [];
    const automationTools = toolSet.tools.map(tool => ({
      ...tool,
      type: 'function_automation',
    }));
    const tools = [...legacyTools, ...automationTools];
    return tools.find(tool => tool.id === Number(toolId)) ?? {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getExecutions: $state => $state.executions.records,
  getExecutionsMeta: $state => $state.executions.meta,
  getExecutionsUIFlags: $state => $state.executions.uiFlags,
};

export const actions = {
  get: async ({ commit }, botId) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isFetching: true });
    try {
      const { data } = await new WgptBotToolsAPI(botId).get();
      commit(types.SET_WGPT_BOT_TOOLS, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isFetching: false });
    }
    return null;
  },
  create: async ({ commit }, { botId, ...createObj }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isCreating: true });
    try {
      const { data } = await new WgptBotToolsAPI(botId).create(createObj);
      commit(types.ADD_WGPT_BOT_TOOL, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isCreating: false });
    }
    return null;
  },
  update: async ({ commit }, { botId, id, ...updateObj }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await new WgptBotToolsAPI(botId).update(id, updateObj);
      commit(types.EDIT_WGPT_BOT_TOOL, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  delete: async ({ commit }, { botId, id }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isDeleting: true });
    try {
      await new WgptBotToolsAPI(botId).delete(id);
      commit(types.DELETE_WGPT_BOT_TOOL, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isDeleting: false });
    }
    return null;
  },
  show: async ({ commit }, { botId, id }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isFetchingItem: true });
    try {
      const { data } = await new WgptBotToolsAPI(botId).show(id);
      commit(types.ADD_WGPT_BOT_TOOL, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isFetchingItem: false });
    }
    return null;
  },
  clone: async ({ commit }, { botId, id }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isCloning: true });
    try {
      const { data } = await new WgptBotToolsAPI(botId).clone(id);
      commit(types.ADD_WGPT_BOT_TOOL, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isCloning: false });
    }
    return null;
  },
  addTool: async ({ commit }, { botId, toolSetId, ...createObj }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await new WgptBotToolsAPI(botId).addTool(
        toolSetId,
        createObj
      );
      commit(types.EDIT_WGPT_BOT_TOOL, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  updateTool: async ({ commit }, { botId, toolSetId, id, ...updateObj }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await new WgptBotToolsAPI(botId).updateTool(
        toolSetId,
        id,
        updateObj
      );
      commit(types.EDIT_WGPT_BOT_TOOL, data);
      return data.tools.find(({ id: toolId }) => toolId === id) ?? {};
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  deleteTool: async ({ commit }, { botId, toolSetId, id }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await new WgptBotToolsAPI(botId).deleteTool(
        toolSetId,
        id
      );
      commit(types.EDIT_WGPT_BOT_TOOL, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  cloneTool: async ({ commit }, { botId, toolSetId, id }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await new WgptBotToolsAPI(botId).cloneTool(
        toolSetId,
        id
      );
      commit(types.EDIT_WGPT_BOT_TOOL, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  addFile: async ({ commit }, { botId, toolSetId, id, file }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: true });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await new WgptBotToolsAPI(botId).addFile(
        toolSetId,
        id,
        formData
      );
      commit(types.EDIT_WGPT_BOT_TOOL, data);
      const tool = data.tools.find(({ id: toolId }) => toolId === id) ?? {};
      const files = tool.files ?? [];
      return files[files.length - 1];
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  testTool: async ({ commit }, { botId, toolSetId, ...data }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isTesting: true });
    try {
      const { data: result } = await new WgptBotToolsAPI(botId).testTool(
        toolSetId,
        data
      );
      return result;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isTesting: false });
    }
    return null;
  },
  export: async ({ commit }, { botId, id }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isExporting: true });
    try {
      const { data } = await new WgptBotToolsAPI(botId).export(id);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isExporting: false });
    }
    return null;
  },
  import: async ({ commit }, { botId, file }) => {
    commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isImporting: true });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await new WgptBotToolsAPI(botId).import(formData);
      commit(types.ADD_WGPT_BOT_TOOL, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_UI_FLAG, { isImporting: false });
    }
    return null;
  },
  getExecutions: async (
    { commit },
    { botId, page = 1, toolSetId, from, to }
  ) => {
    commit(types.SET_WGPT_BOT_TOOL_EXECUTIONS_UI_FLAG, { isFetching: true });
    try {
      const params = { page };
      if (toolSetId) params.tool_set_id = toolSetId;
      if (from) params.from = from;
      if (to) params.to = to;
      if (from || to) {
        params.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      const response = await new WgptBotToolsAPI(botId).getExecutions(params);
      commit(types.SET_WGPT_BOT_TOOL_EXECUTIONS_META, response.data.meta);
      commit(types.SET_WGPT_BOT_TOOL_EXECUTIONS, response.data.payload);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOT_TOOL_EXECUTIONS_UI_FLAG, { isFetching: false });
    }
  },
  clearExecutions: ({ commit }) => {
    commit(types.SET_WGPT_BOT_TOOL_EXECUTIONS, []);
    commit(types.SET_WGPT_BOT_TOOL_EXECUTIONS_META, {});
  },
};

export const mutations = {
  [types.SET_WGPT_BOT_TOOL_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.ADD_WGPT_BOT_TOOL]: MutationHelpers.setSingleRecord,
  [types.SET_WGPT_BOT_TOOLS]: MutationHelpers.set,
  [types.EDIT_WGPT_BOT_TOOL]: MutationHelpers.update,
  [types.DELETE_WGPT_BOT_TOOL]: MutationHelpers.destroy,
  [types.SET_WGPT_BOT_TOOL_EXECUTIONS_UI_FLAG]($state, data) {
    $state.executions.uiFlags = { ...$state.executions.uiFlags, ...data };
  },
  [types.SET_WGPT_BOT_TOOL_EXECUTIONS_META]($state, data) {
    $state.executions.meta = data;
  },
  [types.SET_WGPT_BOT_TOOL_EXECUTIONS]($state, data) {
    $state.executions.records = data;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
